// src/services/assets/assetReplayService.js

/**
 * =====================================================
 * ASSET REPLAY SERVICE
 * =====================================================
 *
 * PURPOSE:
 * - Read-only query + replay layer
 * - Derives current holder from ledger
 * - Verifies holder consistency
 * - Feeds PendingTransfers + ReceiveReturns UI
 *
 * RULES:
 * - All queries on workflow_state — not legacy .status
 * - No raw status strings
 * - getPendingTransfers → PENDING_ACCEPTANCE only
 * - getReturnsPending → RETURN_PENDING only
 * - Replay engine uses WORKFLOW_STATES.FINALIZED
 * - company_id enforced everywhere
 * =====================================================
 */

import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    doc,
    getDoc,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
    WORKFLOW_STATES,
} from "../../services/assets/workflowStates";

import {
    WORKFLOW_BUCKETS,
} from "../../services/assets/workflowBuckets";

import {
    HOLDER_TYPES,
} from "../../constants/holderTypes";

// =====================================================
// COLLECTIONS
// =====================================================

const ASSET_MASTER_COLLECTION =
    "asset_master";

const ASSET_TRANSACTION_COLLECTION =
    "asset_transactions";

// =====================================================
// INTERNAL — VALIDATE COMPANY
// =====================================================

const validateCompany = (companyId) => {
    if (!companyId) {
        throw new Error("company_id is required");
    }
};

// =====================================================
// GET ASSET MASTER
// =====================================================

export const getAssetById = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    if (!assetId) {
        throw new Error("assetId is required");
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        assetId
    );

    const assetSnap = await getDoc(assetRef);

    if (!assetSnap.exists()) {
        throw new Error("Asset not found");
    }

    const assetData = assetSnap.data();

    if (assetData.company_id !== companyId) {
        throw new Error("Unauthorized company access");
    }

    return { id: assetSnap.id, ...assetData };
};

// =====================================================
// GET ALL ASSET TRANSACTIONS
// Full ledger — used by replay engine
// =====================================================

export const getAssetReplayTransactions = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    if (!assetId) {
        throw new Error("assetId is required");
    }

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("asset_id",   "==", assetId),
        where("company_id", "==", companyId),
        orderBy("created_at", "asc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
    }));
};

// =====================================================
// GET FINALIZED TRANSACTIONS
// Only FINALIZED workflow_state — these changed custody
// =====================================================

export const getApprovedTransactions = async (
    assetId,
    companyId
) => {

    const transactions =
        await getAssetReplayTransactions(assetId, companyId);

    // ─────────────────────────────────────────────
    // Filter on workflow_state — not legacy .status
    // Only FINALIZED transactions changed custody
    // ─────────────────────────────────────────────

    return transactions.filter(
        (t) => t.workflow_state === WORKFLOW_STATES.FINALIZED
    );
};

// =====================================================
// GET LATEST FINALIZED TRANSACTION
// Used for current holder derivation
// =====================================================

export const getLatestApprovedTransaction = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    // ─────────────────────────────────────────────
    // Query on workflow_state: FINALIZED
    // Not legacy .status field
    // ─────────────────────────────────────────────

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("asset_id",       "==", assetId),
        where("company_id",     "==", companyId),
        where("workflow_state", "==", WORKFLOW_STATES.FINALIZED),
        orderBy("created_at", "desc"),
        limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;

    const latestDoc = snapshot.docs[0];

    return { id: latestDoc.id, ...latestDoc.data() };
};

// =====================================================
// DERIVE CURRENT HOLDER
// Source of truth: latest FINALIZED transaction
// Fallback: asset_master snapshot
// =====================================================

export const getCurrentHolder = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const latestFinalized =
        await getLatestApprovedTransaction(assetId, companyId);

    // ─────────────────────────────────────────────
    // No finalized transaction yet
    // Fall back to asset_master snapshot
    // ─────────────────────────────────────────────

    if (!latestFinalized) {

        const asset = await getAssetById(assetId, companyId);

        return {
            holder_type:
                asset.current_holder_type ||
                HOLDER_TYPES.STORE,

            holder_id:
                asset.current_holder_id ||
                null,

            source: "asset_master",
        };
    }

    // ─────────────────────────────────────────────
    // Derive from latest finalized transaction
    // ─────────────────────────────────────────────

    return {
        holder_type:      latestFinalized.to_type,
        holder_id:        latestFinalized.to_id,
        transaction_id:   latestFinalized.id,
        transaction_date: latestFinalized.created_at,
        source:           "finalized_transaction",
    };
};

// =====================================================
// FULL REPLAY HISTORY
// Reconstructs complete custody chain from ledger
// =====================================================

export const replayAssetHistory = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const asset =
        await getAssetById(assetId, companyId);

    const transactions =
        await getAssetReplayTransactions(assetId, companyId);

    // ─────────────────────────────────────────────
    // Sort by created_at asc — replay order
    // ─────────────────────────────────────────────

    const sortedTransactions = [...transactions].sort((a, b) => {

        const aTime = a.created_at?.seconds || 0;
        const bTime = b.created_at?.seconds || 0;

        if (aTime !== bTime) return aTime - bTime;

        return a.id.localeCompare(b.id);
    });

    // ─────────────────────────────────────────────
    // REPLAY ENGINE
    // Only FINALIZED transactions mutate holder
    // ─────────────────────────────────────────────

    let currentHolder = {
        holder_type:
            asset.current_holder_type ||
            HOLDER_TYPES.STORE,

        holder_id:
            asset.current_holder_id ||
            null,
    };

    const replayTimeline = sortedTransactions.map(
        (transaction, index) => {

            const previousHolder = { ...currentHolder };

            // ─────────────────────────────────────────
            // Only FINALIZED state changes custody
            // REJECTED / CANCELLED — holder unchanged
            // ─────────────────────────────────────────

            if (
                transaction.workflow_state ===
                WORKFLOW_STATES.FINALIZED
            ) {
                currentHolder = {
                    holder_type: transaction.to_type,
                    holder_id:   transaction.to_id,
                };
            }

            return {
                replay_index:     index + 1,
                transaction_id:   transaction.id,
                asset_id:         transaction.asset_id,
                asset_name:       transaction.asset_name,
                movement_type:    transaction.movement_type,
                workflow_state:   transaction.workflow_state,
                workflow_type:    transaction.workflow_type,
                transaction_class: transaction.transaction_class,
                from_type:        transaction.from_type,
                from_id:          transaction.from_id,
                to_type:          transaction.to_type,
                to_id:            transaction.to_id,
                previous_holder:  previousHolder,
                resulting_holder: { ...currentHolder },
                requested_by:     transaction.requested_by,
                approved_by:      transaction.approved_by,
                received_by:      transaction.received_by,
                remarks:          transaction.remarks || "",
                created_at:       transaction.created_at,
                updated_at:       transaction.updated_at,
            };
        }
    );

    const finalizedCount = sortedTransactions.filter(
        (t) => t.workflow_state === WORKFLOW_STATES.FINALIZED
    ).length;

    return {
        asset: {
            id:             asset.id,
            asset_name:     asset.asset_name     || "",
            asset_category: asset.asset_category || "",
            ownership_mode: asset.ownership_mode || "",
            serial_no:      asset.serial_no      || "",
        },
        current_holder:          currentHolder,
        total_transactions:      sortedTransactions.length,
        completed_transactions:  finalizedCount,
        replay_timeline:         replayTimeline,
    };
};

// =====================================================
// GET PENDING TRANSFERS
// ACCEPTANCE_REQUIRED workflow only
// visibility_bucket: PENDING_TRANSFERS
// Used by: PendingTransfers.js
// =====================================================

export const getPendingTransfers = async (companyId) => {

    validateCompany(companyId);

    // ─────────────────────────────────────────────
    // Query on workflow_state + visibility_bucket
    // Strictly PENDING_ACCEPTANCE only
    // Never mixes with RETURN_PENDING
    // ─────────────────────────────────────────────

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("company_id",       "==", companyId),
        where("workflow_state",   "==", WORKFLOW_STATES.PENDING_ACCEPTANCE),
        where("visibility_bucket","==", WORKFLOW_BUCKETS.PENDING_TRANSFERS),
        orderBy("created_at", "desc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
    }));
};

// =====================================================
// GET PENDING RETURNS
// RETURN_APPROVAL workflow only
// visibility_bucket: RECEIVE_RETURNS
// Used by: ReceiveReturns.js
// =====================================================

export const getPendingReturns = async (companyId) => {

    validateCompany(companyId);

    // ─────────────────────────────────────────────
    // Query on workflow_state + visibility_bucket
    // Strictly RETURN_PENDING only
    // Never mixes with PENDING_ACCEPTANCE
    // ─────────────────────────────────────────────

    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("company_id",       "==", companyId),
        where("workflow_state",   "==", WORKFLOW_STATES.RETURN_PENDING),
        where("visibility_bucket","==", WORKFLOW_BUCKETS.RECEIVE_RETURNS),
        orderBy("created_at", "desc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
    }));
};

// =====================================================
// HOLDER HISTORY
// Sequence of all custody changes — FINALIZED only
// =====================================================

export const getHolderHistory = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const finalized =
        await getApprovedTransactions(assetId, companyId);

    const sorted = [...finalized].sort((a, b) => {
        const aTime = a.created_at?.seconds || 0;
        const bTime = b.created_at?.seconds || 0;
        return aTime - bTime;
    });

    return sorted.map((transaction, index) => ({
        sequence:       index + 1,
        holder_type:    transaction.to_type,
        holder_id:      transaction.to_id,
        holder_name:    transaction.to_holder_name || transaction.to_id,
        transaction_id: transaction.id,
        workflow_type:  transaction.workflow_type,
        changed_at:     transaction.created_at,
        approved_by:    transaction.approved_by || transaction.received_by,
    }));
};

// =====================================================
// VERIFY HOLDER CONSISTENCY
// Compares asset_master snapshot vs ledger replay
// =====================================================

export const verifyHolderConsistency = async (
    assetId,
    companyId
) => {

    validateCompany(companyId);

    const asset =
        await getAssetById(assetId, companyId);

    const replayHolder =
        await getCurrentHolder(assetId, companyId);

    const snapshotHolder = {
        holder_type: asset.current_holder_type,
        holder_id:   asset.current_holder_id,
    };

    const replaySafe =
        String(snapshotHolder.holder_type || "") ===
        String(replayHolder.holder_type   || "")
        &&
        String(snapshotHolder.holder_id   || "") ===
        String(replayHolder.holder_id     || "");

    return {
        replay_safe:      replaySafe,
        snapshot_holder:  snapshotHolder,
        replay_holder:    replayHolder,
    };
};